import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { BODY_SMALL, HEADING_2 } from "@/components/marketing/marketingType";
import {
  SECTION_PANEL,
  SECTION_PANEL_WASH,
} from "@/components/marketing/sectionPanel";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import { DotList, RichParagraph } from "./splitText";
import { Lines } from "@/components/marketing/Lines";

type Split = Laid<"split">;

/**
 * `.alter-benefit_list`: the run of cards under a "Why Choose Webshare"
 * heading is one column with a gutter, so each card carries the gutter under
 * it rather than the section break the rest of the page is set on.
 */
const RUN_GAP = {
  pb: "48px",
  [MID]: { pb: "24px" },
  [TINY]: { pb: "40px" },
} satisfies SxProps<Theme>;

/** `.alter-why-card_wrap`: a rule around two halves, on the run's own measure. */
const CARD = {
  display: "flex",
  width: 1,
  maxWidth: "1088px",
  mx: "auto",
  borderRadius: "16px",
  border: `1px solid ${marketingPalette.wash}`,
  // The rule is drawn around the tinted half, so the corners have to clip it.
  overflow: "hidden",
} satisfies SxProps<Theme>;

/** Stacked, the copy leads whichever side it takes in the row. */
const CARD_STACK = { [MID]: { flexDirection: "column" } } as const;
const CARD_STACK_REVERSED = {
  [MID]: { flexDirection: "column-reverse" },
} as const;

/** `.alter-why-card_left`: the tinted half, its copy centred against the art. */
const CARD_COPY = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "16px",
  flex: "1 1 0",
  minWidth: 0,
  p: "40px",
  backgroundColor: marketingPalette.surface,
  [MID]: { p: "24px" },
} satisfies SxProps<Theme>;

/** `.alter-why-card_right`: the white half, which the art alone fills. */
const CARD_ART = {
  flex: "1 1 0",
  minWidth: 0,
  p: "40px",
  [MID]: { p: "24px" },
} satisfies SxProps<Theme>;

/**
 * `.alter-why-card_visual`: the art takes the half's whole height, so a card
 * the copy makes taller letterboxes its picture rather than leaving it short.
 * Stacked, the half has no height of its own and the picture keeps its own.
 */
const CARD_VISUAL = { width: 1, height: 1, objectFit: "contain" } as const;

/** `.h3_new`: a card's own heading, a size under a section's. */
const CARD_HEADING = {
  color: marketingPalette.ink,
  fontSize: 32,
  lineHeight: "41.6px",
  fontWeight: 500,
  letterSpacing: "-0.04em",
  [TINY]: { fontSize: 24, lineHeight: "32px" },
} satisfies SxProps<Theme>;

/**
 * `.alter-why-card_wrap`: the "Start for free" / "Free Chrome extension" cards
 * the alternative pages run under their "Why Choose Webshare" heading. Copy on
 * a tint, art on white, and the pair inside one rule.
 */
export function WhyCard({ block }: { block: Split }) {
  const copy = (
    <Box sx={CARD_COPY}>
      <Typography component="h3" sx={CARD_HEADING}>
        <Lines text={block.title} />
      </Typography>
      {block.paragraphs?.map((paragraph, index) => (
        <RichParagraph
          key={index}
          text={paragraph}
          links={block.links}
          sx={BODY_SMALL}
        />
      ))}
    </Box>
  );
  const art = (
    <Box sx={CARD_ART}>
      {block.image ? <LandingImage src={block.image} sx={CARD_VISUAL} /> : null}
    </Box>
  );
  return (
    <LandingSection padding="none" sx={RUN_GAP}>
      <Box sx={[CARD, block.reversed ? CARD_STACK_REVERSED : CARD_STACK]}>
        {block.reversed ? art : copy}
        {block.reversed ? copy : art}
      </Box>
    </LandingSection>
  );
}

/**
 * `.main_header_box.is-alternative`: the "What is X?" card. Unlike the product
 * pages' box it is set on a measure of its own rather than the container's,
 * and its art column is a ratio rather than a fixed square.
 */
const ALT_BOX = {
  display: "grid",
  gridTemplateColumns: "1.75fr 1fr",
  alignItems: "center",
  gap: "32px",
  width: 1,
  maxWidth: "1152px",
  mx: "auto",
  [NOT_WIDE]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
} satisfies SxProps<Theme>;

const ALT_COPY = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  minWidth: 0,
  [NOT_WIDE]: { width: 1 },
} satisfies SxProps<Theme>;

/** Square at every step, and three fifths of the column once it stacks. */
const ALT_ART = {
  width: 1,
  aspectRatio: "1",
  objectFit: "contain",
  [NOT_WIDE]: { width: "60%" },
  [MID]: { width: 1 },
} satisfies SxProps<Theme>;

export function AlternativeBox({ block }: { block: Split }) {
  const [lead, ...rest] = block.paragraphs ?? [];
  return (
    // `.container_new.padding-bottom`: the section's short close.
    <LandingSection padding="break">
      <Box sx={[ALT_BOX, SECTION_PANEL, SECTION_PANEL_WASH]}>
        <Box sx={ALT_COPY}>
          <Typography component="h2" sx={HEADING_2}>
            <Lines text={block.title} />
          </Typography>
          {/* The list belongs to the sentence introducing it, so it follows
              the first paragraph rather than all of them. */}
          {lead ? (
            <RichParagraph text={lead} links={block.links} sx={BODY_SMALL} />
          ) : null}
          {block.bullets?.length ? <DotList items={block.bullets} /> : null}
          {rest.map((paragraph, index) => (
            <RichParagraph
              key={index}
              text={paragraph}
              links={block.links}
              sx={BODY_SMALL}
            />
          ))}
        </Box>
        {block.image ? <LandingImage src={block.image} sx={ALT_ART} /> : null}
      </Box>
    </LandingSection>
  );
}
